import { Check, Close, ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import {
  AccordionGeneralityForm,
  SearchPersonTableVM,
} from 'civil-status/view-models/accordion-generality-form';
import { Fragment, memo, useMemo, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import FormGenerality from './form';
import SearchPerson from './search-person';
import { HttpResponse } from 'shell/models/http-response';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { Parenthood, ResponseAnprData, SubjectData } from 'shell/models/person';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import {
  LastStepVM,
  ParentVM,
  SearchPersonVM,
  SectionVM,
} from 'civil-status/view-models/step-form';
import {
  initialConditionsItem,
  initialValuesParent,
  initialValuesSections,
} from 'civil-status/constants/initial-values-step';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { toast } from 'react-toastify';
import { transformerSearchPerson } from 'civil-status/utils/transformer-search-person';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { ErrorWarningToVisualizeType } from 'civil-status/pages/step';
import { DateTime } from 'luxon';
import { Address } from 'shell/models/person/residence';
import { Grid } from '@mui/material';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import TableSearchPerson from './table-search-person';
import { touchedSubject } from 'civil-status/constants/touched-section-subject';
import { KeySections, KeySubjects } from 'civil-status/constants/keys-subjects-section';
import { noop } from 'shell/utils/noop';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import { ID_OTHER_UFC_DELEGATE } from 'civil-status/constants/id-other-ufc-delegate';
import { keyChangeActFromSubject } from 'civil-status/utils/key-change-act-from-subject';
import { clearSection } from 'civil-status/utils/clear-section';
import { keyChangeSpouseFromSubject } from 'civil-status/utils/key-change-spouse-from-subject';
import { withInterpreteCheck } from 'civil-status/utils/with-interprete-check';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { ISTAT_MILANO } from 'shell/constants/istat-milano';

export type SearchPersonType = {
  body?: Array<SearchPersonTableVM>;
  problems?: SaveDataAnpr;
  generality?: AccordionGeneralityForm;
  actBorn?: SectionVM;
  spouse?: ParentVM;
  civilUnionSpouse?: ParentVM;
  father?: ParentVM;
  mother?: ParentVM;
  motivoCancellazione?: string;
};

interface SubjectProps {
  formContext: FormikProps<LastStepVM>;
  title?: string;
  keySubject: string;
  configuration?: string;
  presenza?: string;
  onSaveSubject: (values: ParentVM) => ResponsePromise<number | undefined> | undefined | void;
  codeSubject?: string;
  codUseCase?: string;
  flagDichiarante?: string;
  flagFirmatario?: string;
  tipoSoggetto?: string;
  withoutSearch?: boolean;
  idEvento?: number;
  disabled?: boolean;
  dateFormationAct?: string;
  withReasonDelay?: boolean;
  setDataInterprete: (isWithInterprete?: boolean) => void;
  controllIntNasCivilStatus: (value?: string) => void;
  controllDecedutoCivilStatus: (value?: string) => void;
  controllIsChangeSurnameName: (isChangeSurnameName?: boolean) => void;
  isStepFromEmergency?: boolean;
  isLastStepEmergency?: boolean;
  tipoEvento?: string;
}

const SubjectComponent = ({
  formContext,
  title = '',
  keySubject = '',
  configuration = '', // tipo di configurazione dei dati
  presenza = '',
  onSaveSubject,
  codeSubject,
  codUseCase,
  flagDichiarante,
  flagFirmatario,
  tipoSoggetto,
  withoutSearch = false,
  idEvento,
  disabled,
  dateFormationAct,
  withReasonDelay,
  setDataInterprete,
  controllIntNasCivilStatus,
  controllDecedutoCivilStatus,
  controllIsChangeSurnameName,
  isStepFromEmergency,
  isLastStepEmergency,
  tipoEvento,
}: SubjectProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(false);
  // stato per accordion rierca persona
  const [openSearchPerson, setOpenSearchPerson] = useState<boolean>(false);
  // tabella più soggetti trovati per accordion rierca persona
  const [bodySearchPerson, setBodySearchPerson] = useState<Array<SearchPersonTableVM> | undefined>(
    undefined
  );
  // stato per errori e warning ricerca persona
  const [errorWarningToVisualize, setErrorWarningToVisualize] =
    useState<ErrorWarningToVisualizeType>({
      visibilityError: false,
      titleError: '',
      messagesError: [],
      visibilityWarning: false,
      titleWarning: '',
      messagesWarning: [],
    });

  const values = (formContext.values[keySubject] || {}) as ParentVM;

  const valuesFather: ParentVM = useMemo(() => {
    return (formContext.values[KeySubjects.keyPadre] ||
      formContext.values[KeySubjects.keyProcPa]) as ParentVM;
  }, [formContext.values[KeySubjects.keyPadre], formContext.values[KeySubjects.keyProcPa]]);

  const valuesMother: ParentVM = useMemo(() => {
    return (formContext.values[KeySubjects.keyMadre] ||
      formContext.values[KeySubjects.keyProcMa]) as ParentVM;
  }, [formContext.values[KeySubjects.keyMadre], formContext.values[KeySubjects.keyProcMa]]);

  const { userProfile } = useUserProfile();

  const getPerson = useHttpClient<HttpResponse<ResponseAnprData>, SearchPersonType>(
    `${__ACT_API_URL__}/soggetto/ricercaSoggetti`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: (response) => {
        const subjects = response.data.payload?.risposta3002OK?.listaSoggetti.datiSoggetto || [];

        const res: SearchPersonType = {
          body: undefined,
          generality: undefined,
          problems: undefined,
          actBorn: undefined,
          spouse: undefined,
          civilUnionSpouse: undefined,
        };

        if (
          response.data.payload?.risposta3002OK?.listaAnomalie?.errore ||
          response.data.payload?.rispostaKO?.listaErrori?.errore
        ) {
          // controllo errori

          res.problems = {
            warnings: (response.data.payload?.risposta3002OK?.listaAnomalie?.errore || []).map(
              (warning) => ({
                id: warning.codiceErroreAnomalia,
                value: warning.testoErroreAnomalia,
              })
            ),
            errors: (response.data.payload?.rispostaKO?.listaErrori?.errore || []).map((error) => ({
              id: error.codiceErroreAnomalia,
              value: error.testoErroreAnomalia,
            })),
          };
        }

        if (subjects.length > 1) {
          // trovati più soggetti mostrare tabella

          res.body = subjects.map((subject) => {
            let residence = '';

            let state = '';

            const residenceArray = subject?.residenza || [];

            const enrollData = subject?.datiIscrizione;

            // state check for object
            if (residenceArray[0]?.indirizzo?.toponimo) {
              if (
                residenceArray[0]?.indirizzo?.comune?.codiceIstat ===
                userProfile?.datiLogin?.codiceIstat
              ) {
                state = 'Residente';
              } else {
                state = `Residente a ${residenceArray[0]?.indirizzo?.comune?.nomeComune || ''}`;
              }
            }

            if (subject?.generalita?.soggettoAIRE && subject?.generalita?.soggettoAIRE === 'S') {
              if (
                (residenceArray[0]?.localitaEstera?.indirizzoEstero ||
                  residenceArray[0]?.localitaEstera?.consolato) &&
                enrollData?.datiIscrizioneComune?.comune?.codiceIstat !== ISTAT_MILANO
              ) {
                state = `Iscritto AIRE - ${
                  enrollData?.datiIscrizioneComune?.comune?.nomeComune || ''
                }`;
              } else {
                state = 'Iscritto AIRE';
              }
            }

            if (
              subject?.datiCancellazione &&
              subject?.datiCancellazione?.descrizioneMotivoCancellazione
            ) {
              state = `Cancellato per: ${subject.datiCancellazione.descrizioneMotivoCancellazione}`;
            }

            if (subject?.descrizioneStatusPersonaSipo) {
              state = subject.descrizioneStatusPersonaSipo;
            }

            if ((subject?.residenza || [])[0]?.indirizzo?.comune?.nomeComune) {
              // valorizzare con comune
              const indirizzo: Address = (subject.residenza || [])[0]?.indirizzo || {};

              residence = indirizzo?.comune?.nomeComune
                ? `${indirizzo.comune.nomeComune || ''} ${
                    indirizzo.comune.siglaProvinciaIstat
                      ? `(${indirizzo.comune.siglaProvinciaIstat})`
                      : ''
                  }`
                : '';
            } else {
              const indirizzo: Address = (subject.residenza || [])[0]?.indirizzo || {};
              // valorizzare con toponimo
              residence =
                indirizzo?.toponimo?.denominazioneToponimo || indirizzo?.numeroCivico?.numero
                  ? `${indirizzo?.toponimo?.denominazioneToponimo || ''},${
                      indirizzo?.numeroCivico?.numero
                    }${
                      indirizzo.numeroCivico?.esponente1 || indirizzo.numeroCivico?.lettera
                        ? `/${indirizzo.numeroCivico?.esponente1 || ''}${
                            indirizzo.numeroCivico?.lettera || ''
                          }`
                        : ''
                    }`
                  : '';
            }

            let placeBorn = '';
            // placeBorn check for object
            if (subject.generalita?.luogoNascita?.localita) {
              placeBorn = `${subject.generalita?.luogoNascita?.localita.descrizioneLocalita} ${
                subject.generalita?.luogoNascita?.localita.descrizioneStato
                  ? `(${subject.generalita?.luogoNascita?.localita.descrizioneStato})`
                  : ''
              }`;
            }

            if (subject.generalita?.luogoNascita?.comune) {
              placeBorn = `${subject.generalita?.luogoNascita?.comune.nomeComune} ${
                subject.generalita?.luogoNascita?.comune.siglaProvinciaIstat
                  ? `(${subject.generalita?.luogoNascita?.comune.siglaProvinciaIstat})`
                  : ''
              }`;
            }

            return {
              fiscalCode: subject.generalita?.codiceFiscale?.codFiscale || '',
              surname: subject.generalita?.cognome || '',
              name: subject.generalita?.nome || '',
              gender: subject.generalita?.sesso || '',
              placeBorn,
              dateBorn: subject.generalita?.dataNascita
                ? DateTime.fromISO(subject.generalita?.dataNascita).toFormat('dd/MM/yyyy')
                : '',
              residence,
              state,
              idAnpr: subject.generalita?.idSchedaSoggettoANPR || '',
              motivoCancellazione: subject.datiCancellazione?.motivoCancellazione || '',
              remoteData: subject,
            } as SearchPersonTableVM;
          });
        }

        if (subjects.length === 1) {
          // trovato un solo soggetto

          const [subject] = subjects;

          const generalityActBornAnpr = transformerSearchPerson(subject || {});

          res.generality = generalityActBornAnpr.generality;
          res.actBorn = generalityActBornAnpr.actBorn;
          res.spouse = generalityActBornAnpr.spouse;
          res.civilUnionSpouse = generalityActBornAnpr.civilUnionSpouse;
          res.father = generalityActBornAnpr.father;
          res.mother = generalityActBornAnpr.mother;
          res.motivoCancellazione = subject.datiCancellazione?.motivoCancellazione;
        }

        return res;
      },
    }
  );

  const accordionParenthood = useEventCallback(
    (response: SearchPersonType, searchedFiscalCode?: string) => {
      // La ricerca in configurazione famiglia restituisce l'intero nucleo: i soggetti grezzi
      // sono disponibili in body[].remoteData (SubjectData completo, con maternità/paternità).
      const subjects = response.body || [];

      if (subjects.length === 0) {
        return;
      }

      const parent = (formContext.values[KeySections.keyGenitCheTrasmetteCit] as SectionVM)
        ?.parentTransmittedCitizenship;
      //1 madre, 2 padre, 3 entrambi

      if (!parent) {
        return;
      }

      // Soggetto su cui è stata effettuata la ricerca (match per codice fiscale)
      const mainSubject = subjects.find(
        (subject) => subject.fiscalCode === searchedFiscalCode
      )?.remoteData;

      if (!mainSubject) {
        return;
      }

      // Confronta nome e cognome di maternità/paternità del soggetto con gli altri
      // componenti della famiglia per individuare madre e padre.
      const findParentSubject = (parenthood?: Parenthood): SubjectData | undefined => {
        const name = parenthood?.generalita?.nome?.trim().toLowerCase();
        const surname = parenthood?.generalita?.cognome?.trim().toLowerCase();

        if (!name || !surname) {
          return undefined;
        }

        return subjects.find(
          (subject) =>
            subject.fiscalCode !== searchedFiscalCode &&
            subject.name?.trim().toLowerCase() === name &&
            subject.surname?.trim().toLowerCase() === surname
        )?.remoteData;
      };

      let newValues = { ...formContext.values };

      const applyParent = (key: string, parentSubject?: SubjectData) => {
        if (!parentSubject) {
          return;
        }

        const parentData = transformerSearchPerson(parentSubject);

        if (!parentData.generality) {
          return;
        }

        const current = (formContext.values[key] || {}) as ParentVM;
        const fiscalCode = parentSubject.generalita?.codiceFiscale?.codFiscale;

        newValues = {
          ...newValues,
          [key]: {
            ...current,
            searchPerson: fiscalCode ? { fiscalCode } : current.searchPerson,
            generality: parentData.generality || current.generality,
          } as ParentVM,
        };
      };

      if (parent === '1' || parent === '3') {
        applyParent(KeySubjects.keyMadre, findParentSubject(mainSubject.maternita));
      }
      if (parent === '2' || parent === '3') {
        applyParent(KeySubjects.keyPadre, findParentSubject(mainSubject.paternita));
      }

      formContext.setValues(newValues);
    }
  );

  const onSearchPerson = useEventCallback(
    (valuesSearchPerson?: SearchPersonVM, withTipoUseCase = false) => {
      //Chiamata che effettua la ricerca
      const hasSearchBirthFilters = Boolean(
        values.searchPerson?.birthdayDateChoice && values.searchPerson?.birthdayDate
      );

      const hasSectionFamily = Boolean(codeSubject === 'INTEST_CITT');

      // La ricerca famiglia (con tipoUseCase) viene effettuata solo nella seconda chiamata,
      // dopo aver individuato univocamente il soggetto con la prima ricerca (senza tipoUseCase)
      const isFamilySearch = hasSectionFamily && withTipoUseCase;

      // Codice fiscale del soggetto cercato: stesso criterio usato per entrambe le chiamate,
      // così la seconda (con tipoUseCase) individua esattamente lo stesso soggetto della prima
      const searchedFiscalCode =
        valuesSearchPerson?.fiscalCode || values?.searchPerson?.fiscalCode || undefined;

      // datiRichiesta standard
      const datiRichiesta = {
        ricercaNazionale: '1',
      };

      // Criteri di ricerca per la configurazione famiglia (parentela/genitori)
      const datiRichiestaFamily = {
        datiAnagraficiRichiesti: ['02', '10', '21', '22'],
        ricercaNazionale: '0',
        schedaAnagraficaRichiesta: '1',
      };

      const data = {
        configurazione: configuration,
        usecase: (formContext?.values as SectionVM)?.actForm?.useCase,
        tipoUseCase: isFamilySearch ? codUseCase : undefined,
        datiNascita: hasSearchBirthFilters
          ? {
              senzaGiorno:
                !values.searchPerson?.birthdayDateChoice?.includes('day') &&
                values.searchPerson?.birthdayDateChoice?.includes('month')
                  ? '1'
                  : undefined,
              senzaGiornoMese:
                !values.searchPerson?.birthdayDateChoice?.includes('day') &&
                !values.searchPerson?.birthdayDateChoice?.includes('month')
                  ? '1'
                  : undefined,
              dataEvento: values.searchPerson?.birthdayDate?.toFormat('yyyy-MM-dd') || undefined,
            }
          : undefined,
        criteriRicerca: {
          codiceFiscale: searchedFiscalCode,
          nome: valuesSearchPerson?.name || values?.searchPerson?.name || undefined,
          cognome: valuesSearchPerson?.surname || values?.searchPerson?.surname || undefined,
          senzaCognome:
            values?.searchPerson?.withoutSurname || !values?.searchPerson?.surname
              ? '1'
              : undefined,
          senzaNome:
            values?.searchPerson?.withoutName || !values?.searchPerson?.name ? '1' : undefined,
          idSchedaSoggettoANPR:
            valuesSearchPerson?.idAnpr || values?.searchPerson?.idAnpr || undefined,
          tipoScheda: isFamilySearch ? '1' : undefined,
        },
        datiRichiesta: isFamilySearch ? datiRichiestaFamily : datiRichiesta,
      };

      (
        getPerson({ data }) as ResponsePromise<HttpResponse<ResponseAnprData>, SearchPersonType>
      ).then((response) => {
        const responseCasted = (response || {}) as SearchPersonType;

        // chiusura accordion di ricerca persone
        setOpenSearchPerson(false);

        if (responseCasted.problems) {
          const lengthError = (responseCasted.problems.errors || [])?.length;
          const titleError = lengthError > 1 ? `Ci sono ${lengthError} errori` : `C'è 1 errore`;

          const lengthWarning = (responseCasted.problems.warnings || [])?.length;
          const titleWarning =
            lengthWarning > 1 ? `Ci sono ${lengthWarning} warnings` : `C'è 1 warning`;

          setErrorWarningToVisualize({
            visibilityError: lengthError >= 1,
            titleError,
            messagesError: responseCasted.problems.errors || [],
            visibilityWarning: lengthWarning >= 1,
            titleWarning,
            messagesWarning: responseCasted.problems.warnings || [],
          });
        } else {
          setErrorWarningToVisualize({
            visibilityError: false,
            visibilityWarning: false,
          });
        }

        if (isBaseDec && responseCasted?.motivoCancellazione === '1') {
          // Per le morti non selezionare un soggetto già morto
          toast.error('Soggetto già morto');
          return;
        }

        if (responseCasted.generality) {
          let newValues = {
            ...formContext.values,
            [keySubject]: {
              ...(values || {}),
              isFromSearchPerson: true,
              searchPerson: undefined,
              generality: {
                ...(responseCasted.generality || {}),
                isRequiredFromOtherAccordion:
                  values?.generality?.isRequiredFromOtherAccordion || false,
                idSubject: values?.generality?.idSubject || undefined,
                isSignatory: values?.generality?.isSignatory || undefined,
                conditionsItem: initialConditionsItem,
                conditionsItemSignatory: initialConditionsItem,
                ufcDelegate:
                  (formContext.values[keySubject] as ParentVM).configuration ===
                  ConfigurationType.OFFI_ACATT
                    ? ID_OTHER_UFC_DELEGATE
                    : (formContext.values[keySubject] as ParentVM).generality?.ufcDelegate || '',
              },
            } as ParentVM,
          };

          // Per alcuni soggetti inizializzare anche l'atto di nascita
          const keyActBorn = keyChangeActFromSubject(keySubject);
          if (keyActBorn) {
            const controllKeyActBorn = Boolean(formContext.values[keyActBorn])
              ? keyActBorn
              : KeySections.keyAttoCollegato;

            newValues = {
              ...newValues,
              [controllKeyActBorn]: {
                ...(formContext.values[controllKeyActBorn] || {}),
                typeAct: responseCasted.actBorn?.typeAct || initialValuesSections.typeAct,
                actForm: {
                  ...(responseCasted.actBorn?.actForm || initialValuesSections.actForm),
                },
              } as SectionVM,
            };
          }

          // Per alcuni soggetti inizializzare anche il coniuge
          const keySpouse = keyChangeSpouseFromSubject(
            keySubject,
            Boolean(responseCasted.civilUnionSpouse)
          );
          if (keySpouse) {
            const currentValuesSpouse = (formContext.values[keySpouse] || {}) as ParentVM;
            const dataSpouse = responseCasted.spouse || responseCasted.civilUnionSpouse;

            newValues = {
              ...newValues,
              [keySpouse]: {
                ...currentValuesSpouse,
                searchPerson: dataSpouse?.searchPerson,
                generality: dataSpouse?.generality || currentValuesSpouse.generality,
              } as ParentVM,
            };
          }

          // un solo soggetto
          formContext.setValues(newValues);
        }

        // Configurazione famiglia: individua e popola madre/padre dal nucleo restituito
        // dalla seconda chiamata (con tipoUseCase)
        if (isFamilySearch) {
          accordionParenthood(responseCasted, searchedFiscalCode);
        }

        // La tabella dei risultati va mostrata solo per la prima chiamata (senza tipoUseCase):
        // la seconda chiamata serve solo a valorizzare gli accordion padre e madre
        if (!isFamilySearch && (responseCasted.body || [])?.length > 0) {
          setBodySearchPerson(responseCasted.body);
        } else {
          setBodySearchPerson(undefined);
        }

        if (!withTipoUseCase && hasSectionFamily && responseCasted.generality) {
          onSearchPerson(
            {
              fiscalCode: responseCasted.generality?.fiscalCode,
              name: responseCasted.generality?.name,
              surname: responseCasted.generality?.surname,
            },
            true
          );
        }
      });
    }
  );

  const onChangeSearchPerson = useEventCallback(
    (key: keyof SearchPersonVM, value: SearchPersonVM[keyof SearchPersonVM]) => {
      formContext.setFieldValue(keySubject, {
        ...(values || {}),
        searchPerson: {
          ...(values?.searchPerson || {}),
          [key]: value,
        },
      });
    }
  );

  const onChangesSearchPerson = useEventCallback((valuesSearchPerson: SearchPersonVM) => {
    formContext.setFieldValue(keySubject, {
      ...(values || {}),
      searchPerson: {
        ...(valuesSearchPerson || {}),
      },
    });
  });

  const onFieldGeneralityChange = useEventCallback(
    (
      key: keyof AccordionGeneralityForm,
      value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
    ) => {
      formContext.setFieldValue(keySubject, {
        ...(values || {}),
        generality: {
          ...(values?.generality || {}),
          [key]: value,
        },
      });
    }
  );

  const onFieldsGeneralityChange = useEventCallback((valuesNew: AccordionGeneralityForm) => {
    formContext.setFieldValue(keySubject, {
      ...(values || {}),
      generality: {
        ...(values?.generality || {}),
        ...valuesNew,
      },
    });
  });

  // const onFieldsChanges = useEventCallback((valuesNew: ParentVM) => {
  //   formContext.setFieldValue(keySubject, valuesNew);
  // });

  const resetForm = useEventCallback(() => {
    formContext.setValues({
      ...formContext.values,
      [keySubject]: {
        ...((formContext.values[keySubject] || {}) as ParentVM),
        searchPerson: undefined,
      },
    });
  });

  interface onCleanInput {
    keyCurrent: string;
    resetIsRequiredFromOtherAccordion?: boolean;
  }

  const onClean = useEventCallback((mantainDataGenarality?: AccordionGeneralityForm) => {
    const onCleanData: Array<onCleanInput> = [{ keyCurrent: keySubject }];

    if (keySubject === KeySubjects.keyDeceduto) {
      // Reset del coniuge
      onCleanData.push({
        keyCurrent: KeySubjects.keyConiuge,
        resetIsRequiredFromOtherAccordion: true,
      } as onCleanInput);
    }

    let newValues = {
      ...(formContext.values || {}),
    };

    onCleanData.forEach((item) => {
      const currentData = (formContext.values[item.keyCurrent] || {}) as ParentVM;

      newValues = {
        ...newValues,
        [item.keyCurrent]: {
          ...initialValuesParent,
          configuration: currentData.configuration,
          codeSubject: currentData.codeSubject,
          flagComparente: currentData?.flagComparente,
          flagDichiarante: currentData?.flagDichiarante,
          tipoSoggetto: currentData?.tipoSoggetto,
          flagFirmatario: currentData?.flagFirmatario,
          presenza: currentData?.presenza,
          generality: {
            ...initialValuesParent.generality,
            ...(mantainDataGenarality || {}),
            isRequiredFromOtherAccordion: item.resetIsRequiredFromOtherAccordion
              ? false
              : currentData?.generality?.isRequiredFromOtherAccordion || false,
          },
        } as ParentVM,
      };
    });

    // pulizia atto relativo al soggetto
    const keyAct = keyChangeActFromSubject(keySubject);
    const currKeyAct = Boolean(formContext.values[keyAct]) ? keyAct : KeySections.keyAttoCollegato;
    const valuesAct = formContext.values[currKeyAct] as SectionVM | undefined;
    if (keyAct && valuesAct) {
      newValues = {
        ...newValues,
        [currKeyAct]: {
          ...initialValuesSections,
          ...clearSection(valuesAct),
          idAttoCollegato: undefined,
        } as SectionVM,
      };
    }

    formContext.setValues(newValues);
  });

  const errorsGenerality: FormikErrors<AccordionGeneralityForm> | undefined = useMemo(() => {
    return (
      ((formContext.errors[keySubject] as FormikErrors<ParentVM>)
        ?.generality as FormikErrors<AccordionGeneralityForm>) || undefined
    );
  }, [formContext.errors]);

  const withErrors: boolean = useMemo(() => {
    if (errorsGenerality && Object.keys(errorsGenerality)?.length > 0) {
      return true;
    }

    return false;
  }, [errorsGenerality]);

  const deleteData = useHttpClient(
    `${__ACT_API_URL__}/soggetto/rimuovi/idEvento/${idEvento}/codiceSoggetto/${codeSubject}/idSoggetto/${
      values?.generality?.idSubject || ''
    }`,
    {
      options: {
        method: 'DELETE',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onDelete = useEventCallback(() => {
    if (!Boolean(values?.generality?.idSubject)) {
      // Se l'idSoggetto non è presente non effettuare pulizia sul DB perchè il soggetto non è stato salvato
      onClean();
      return;
    }

    (deleteData() as unknown as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');

      onClean();
    });
  });

  const onSave = useEventCallback(() => {
    const touched = {
      [keySubject]: {
        ...touchedSubject,
      } as unknown as FormikTouched<ParentVM>,
    } as unknown as FormikTouched<LastStepVM>;

    formContext.setTouched(touched);

    if (withErrors) {
      // se è presente errore non effettuare chiamata e segnalarli con il setTouched
      return;
    }

    onSaveSubject(values)?.then((res) => {
      const idSubject = res as number | undefined;

      const isDichiarante = values?.flagDichiarante === '1';
      const withInterprete = values.generality?.isWithInterprete;

      const isWithInterpreteCheck = withInterpreteCheck(isDichiarante, configuration);

      const valuesInterprete: ParentVM | undefined = formContext.values[KeySubjects.keyInterprete];

      formContext.setValues({
        ...formContext.values,
        [KeySubjects.keyInterprete]: (isWithInterpreteCheck // se è dichiarante effettuare controlli per annullare i dati o mantenerli
          ? {
              ...(valuesInterprete || {}),
              isSaved: valuesInterprete?.isSaved,
              generality: withInterprete
                ? {
                    ...(valuesInterprete?.generality || {}),
                  }
                : {
                    ...initialValuesParent.generality,
                  },
            }
          : {
              ...(valuesInterprete || {}),
            }) as ParentVM,
        [keySubject]: {
          ...((formContext.values[keySubject] || {}) as ParentVM),
          isSaved: true,
          generality: {
            ...(values?.generality || {}),
            idSubject,
          },
        },
      });

      // onFieldsChanges({
      //   ...(values || {}),
      //   isSaved: true,
      //   generality: {
      //     ...(values?.generality || {}),
      //     idSubject,
      //   },
      // });
    });
  });

  const onVisibilityError = useEventCallback(() => {
    setErrorWarningToVisualize({
      ...errorWarningToVisualize,
      visibilityError: false,
      titleError: '',
      messagesError: [],
    });
  });

  const onVisibilityWarning = useEventCallback(() => {
    setErrorWarningToVisualize({
      ...errorWarningToVisualize,
      visibilityWarning: false,
      titleWarning: '',
      messagesWarning: [],
    });
  });

  const isBaseDec = useMemo(() => {
    return configuration === ConfigurationType.BASEDEC;
  }, [configuration]);

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
      >
        <AccordionSummary
          aria-controls={`panel-${keySubject}-content`}
          id={`panel-${keySubject}-header`}
          expandIcon={<ExpandMore />}
        >
          <Typography style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {title}
            &nbsp;
            {withErrors ? <Close color="error" /> : <Check color="success" />}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {open ? (
            <Grid item container spacing={2}>
              {withoutSearch ? undefined : (
                <Grid item xs={12}>
                  <SearchPerson
                    values={values?.searchPerson}
                    onChange={onChangeSearchPerson}
                    onChanges={onChangesSearchPerson}
                    onSearch={() => onSearchPerson()}
                    resetForm={resetForm}
                    setOpen={setOpenSearchPerson}
                    open={openSearchPerson}
                    disabled={disabled}
                  />
                </Grid>
              )}
              {errorWarningToVisualize.visibilityError ? (
                <Grid item xs={12}>
                  <SearchMessages
                    type={MessageType.Error}
                    title={errorWarningToVisualize.titleError || ''}
                    messages={errorWarningToVisualize.messagesError || []}
                    visibility={errorWarningToVisualize.visibilityError || false}
                    onChangeVisibility={onVisibilityError}
                  />
                </Grid>
              ) : undefined}
              {errorWarningToVisualize.visibilityWarning ? (
                <Grid item xs={12}>
                  <SearchMessages
                    type={MessageType.Warning}
                    title={errorWarningToVisualize.titleWarning || ''}
                    messages={errorWarningToVisualize.messagesWarning || []}
                    visibility={errorWarningToVisualize.visibilityWarning || false}
                    onChangeVisibility={onVisibilityWarning}
                    disabled={disabled}
                  />
                </Grid>
              ) : undefined}
              {bodySearchPerson ? (
                <Grid item xs={12}>
                  <TableSearchPerson
                    tableBody={bodySearchPerson}
                    onSearchPerson={onSearchPerson}
                    onVisibilityPressAnsc={noop}
                    isBaseDec={isBaseDec}
                  />
                </Grid>
              ) : undefined}
              <FormGenerality
                formContext={formContext}
                onChangeGenerality={onFieldGeneralityChange}
                onChangeFieldsGenerality={onFieldsGeneralityChange}
                isSaved={values.isSaved}
                generality={values?.generality || undefined}
                errorsGenerality={errorsGenerality}
                withErrors={withErrors}
                touchedGenerality={
                  (formContext.touched[keySubject] as unknown as FormikTouched<ParentVM>)
                    ?.generality as unknown as FormikTouched<AccordionGeneralityForm>
                }
                disabled={disabled}
                configuration={configuration}
                presenza={presenza}
                codeSubject={codeSubject}
                onSave={onSave}
                onDelete={onDelete}
                onClean={onClean}
                flagDichiarante={flagDichiarante}
                flagFirmatario={flagFirmatario}
                tipoSoggetto={tipoSoggetto}
                valuesMother={valuesMother?.generality}
                valuesFather={valuesFather?.generality}
                dateFormationAct={dateFormationAct}
                keySubject={keySubject}
                withReasonDelay={withReasonDelay}
                setDataInterprete={setDataInterprete}
                controllIntNasCivilStatus={controllIntNasCivilStatus}
                controllDecedutoCivilStatus={controllDecedutoCivilStatus}
                controllIsChangeSurnameName={controllIsChangeSurnameName}
                isBaseDec={isBaseDec}
                isStepFromEmergency={isStepFromEmergency}
                isLastStepEmergency={isLastStepEmergency}
                tipoEvento={tipoEvento}
              />
            </Grid>
          ) : undefined}
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

SubjectComponent.displayName = 'Accordion Subject component';

const Subject = memo(SubjectComponent);

export default Subject;
